/**
 * Utility functions for parsing and managing dynamic output declarations in prompts.
 * 
 * Dynamic outputs allow users to declare custom outputs directly in prompts using <<variable>> syntax.
 */

import { ArtifactType } from './artifactTypes';

/**
 * Dynamic output declaration parsed from prompt
 */
export interface DynamicOutput {
  name: string;
  type: ArtifactType;
}

/**
 * Parse dynamic output declarations from prompt content.
 * 
 * Supports two formats:
 * - <<variable>> - defaults to 'string' type
 * - <<variable:type>> - explicit type annotation
 * 
 * @param promptContent - Prompt text to parse
 * @returns Array of parsed dynamic output declarations
 * 
 * @example
 * parseOutputDeclarations("Create <<labels:json>> and <<summary>>")
 * // Returns: [
 * //   { name: "labels", type: "json" },
 * //   { name: "summary", type: "string" }
 * // ]
 */
export function parseOutputDeclarations(promptContent: string): DynamicOutput[] {
  // Pattern: <<variable>> or <<variable:type>>
  // Matches: letters/digits/underscores for variable name
  // Optional: colon followed by type annotation
  const pattern = /<<(\w+)(?::(\w+))?>/g;
  
  const declarations: DynamicOutput[] = [];
  const seen = new Set<string>(); // Track duplicates
  
  let match;
  while ((match = pattern.exec(promptContent)) !== null) {
    const name = match[1];
    const typeStr = match[2];
    
    // Skip if we've already seen this name (only add first occurrence)
    if (seen.has(name)) {
      continue;
    }
    seen.add(name);
    
    // Validate and normalize type
    let type: ArtifactType = 'string'; // default
    if (typeStr) {
      const normalized = typeStr.toLowerCase();
      // Validate against known types
      if (['string', 'file', 'json', 'list', 'vector_db'].includes(normalized)) {
        type = normalized as ArtifactType;
      }
      // If invalid type, default to 'string' (validation will catch this)
    }
    
    declarations.push({ name, type });
  }
  
  return declarations;
}

/**
 * Validate dynamic output declarations against processor metadata.
 * 
 * Checks for:
 * - Naming conflicts with standard outputs
 * - Invalid type annotations
 * - Reserved names
 * 
 * @param declarations - Parsed dynamic outputs
 * @param standardOutputs - Standard output keys from processor metadata
 * @returns Validation result with errors and warnings
 */
export interface OutputValidationResult {
  conflicts: DynamicOutput[];      // Conflicts with standard outputs
  invalidTypes: DynamicOutput[];   // Invalid type annotations
  valid: boolean;                  // Overall validity
}

export function validateDynamicOutputs(
  declarations: DynamicOutput[],
  standardOutputs: string[]
): OutputValidationResult {
  const standardSet = new Set(standardOutputs);
  const validTypes: Set<string> = new Set(['string', 'file', 'json', 'list', 'vector_db']);
  
  const conflicts: DynamicOutput[] = [];
  const invalidTypes: DynamicOutput[] = [];
  
  for (const decl of declarations) {
    // Check for naming conflicts
    if (standardSet.has(decl.name)) {
      conflicts.push(decl);
    }
    
    // Check for invalid types
    if (!validTypes.has(decl.type)) {
      invalidTypes.push(decl);
    }
  }
  
  return {
    conflicts,
    invalidTypes,
    valid: conflicts.length === 0 && invalidTypes.length === 0
  };
}

/**
 * Get a color for dynamic output handles (distinct from standard outputs).
 * Dynamic outputs use purple border to distinguish from standard outputs.
 * 
 * @returns CSS color class for handle border
 */
export function getDynamicOutputBorderColor(): string {
  return 'border-purple-500';
}

/**
 * Get badge CSS class for dynamic output indicator.
 * 
 * @returns CSS class for "⚡ dynamic" badge
 */
export function getDynamicOutputBadgeClass(): string {
  return 'text-purple-600 dark:text-purple-400';
}

